import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactHtmlParser from 'react-html-parser'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { TABLE_CONST } from 'Utils/Constants'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import { CoffeeWebLogoPNG, WaterMarkImageForInvoice } from 'Assets/Icons'
import { commonMethods } from 'Utils/commonMethods'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'

const CancelledInvoiceReports = ({ setLoading, mockCancelledInvoiceReports, mockGetFetchedData }) => {
  const { t } = useTranslation()

  let rowCount = 5
  const windowSize = window.innerWidth
  let paginationButtons = TABLE_CONST.paginationButtonCount
  const isMobileScreen = () => window.innerWidth < TABLE_CONST.maxWidthTab

  const [getFetchedData, setGetFetchedData] = useState([])
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })
  const [invoiceContent, setInvoiceContent] = useState('')
  const [showModal, setShowModal] = useState(false)

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  useEffect(() => {
    getMissingReport()

    // Testing Purpose
    if (mockCancelledInvoiceReports) {
      setGetFetchedData(mockGetFetchedData)
    }
  }, [])

  const getMissingReport = async () => {
    setLoading(true)

    try {
      const response = await apiAdapterCoffeeWeb.getCancelledInvoiceReport()

      if (response?.data?.returnLst?.length) {
        const modifiedData = response.data.returnLst.map((item) => {
          const invoiceDate = commonMethods.utcToLocalTime(item.invoiceDate)

          return {
            ...item,
            invoiceDate
          }
        })

        setGetFetchedData(modifiedData)
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('GET_API_FAILED'),
        onHide: handleDismiss,
        onRetry: getMissingReport
      })
    } finally {
      setLoading(false)
    }
  }

  const getInvoiceNumberContent = async (invoiceNumber) => {
    setLoading(true)

    try {
      const response = await apiAdapterCoffeeWeb.getCancelledInvoices(invoiceNumber)
      const content = response?.data?.returnLst

      if (content) {
        const tempDiv = document.createElement('div')

        tempDiv.innerHTML = content

        const bannerImage = tempDiv.querySelector('.banner-image')

        if (bannerImage) {
          bannerImage.style.transform = 'rotate(-7deg)'
          bannerImage.style.opacity = '0.1'
        }

        const container = tempDiv.querySelector('.container')

        container.style.width = '90%'
        container.style.margin = '10 auto'
        container.style.backgroundImage = `url(${WaterMarkImageForInvoice})`
        container.style.backgroundSize = '255px'

        const updatedContent = tempDiv.innerHTML.replace(/<img[^>]+src="([^">]+)"/g, (match) => match.replace(/src="[^">]+"/, `src="${CoffeeWebLogoPNG}"`)).replace(/rotate:\s*-?\d+deg;/g, '')

        if (isMobileScreen()) {
          downloadInvoice(updatedContent)
        } else {
          setInvoiceContent(updatedContent)
          setShowModal(true)
        }
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('INVOICE_GENERATION_FAILED'),
        onHide: handleDismiss,
        onRetry: getInvoiceNumberContent
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDismiss = () => {
    setShowDialog({
      ...showDialog,
      showModal: false
    })
  }

  const downloadInvoice = async (content) => {
    const clonedElement = document.createElement('div')
    const isMobile = window.innerWidth <= 768

    clonedElement.style.position = 'absolute'
    clonedElement.style.top = '-9999px'
    clonedElement.style.left = '0'

    if (isMobile) {
      clonedElement.style.width = '100vw'
      clonedElement.style.padding = '0'
      clonedElement.style.margin = '0'
      clonedElement.style.boxSizing = 'border-box'
      clonedElement.style.maxWidth = '100%'
      clonedElement.style.transformOrigin = 'top left'
    }
    clonedElement.innerHTML = content
    document.body.appendChild(clonedElement)

    const images = clonedElement.getElementsByTagName('img')
    const imageLoadPromises = Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          // eslint-disable-next-line no-param-reassign
          img.onload = resolve
          // eslint-disable-next-line no-param-reassign
          img.onerror = resolve
          if (img.complete) resolve()
        })
    )

    await Promise.all(imageLoadPromises)

    try {
      const scale = isMobile ? 2 : 2.5

      const canvas = await html2canvas(clonedElement, {
        scale,
        width: clonedElement.scrollWidth,
        height: clonedElement.scrollHeight,
        windowWidth: clonedElement.scrollWidth,
        windowHeight: clonedElement.scrollHeight
      })

      const imgData = canvas.toDataURL('image/png')

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)

      pdf.save('invoice.pdf')
    } catch (err) {
      console.error('Error generating PDF:', err)
    } finally {
      document.body.removeChild(clonedElement)
    }
  }

  const headerElement = (
    <div className="invoice-header-container">
      <span className="font-bold white-space-nowrap">{t('INVOICE')}</span>
      <div className="invoice-button">
        <ButtonComponent type="Custom" label={t('DOWNLOAD_INVOICE')} variant="action" icon="pi pi-arrow-down" onClick={() => downloadInvoice(invoiceContent)} />
      </div>
    </div>
  )

  const invoiceNumberBody = (rowData) => (
    <span onClick={() => getInvoiceNumberContent(rowData.invoiceNo)} className="invoice-number-clickable">
      {rowData.invoiceNo}
    </span>
  )

  const columns = [
    { field: 'invoiceNo', header: t('USER_INVOICE'), className: 'user-invoice-column', body: invoiceNumberBody },
    { field: 'invoiceDate', header: t('INVOICE_DATE'), className: 'invoice-date-column' },
    { field: 'paymentStatus', header: t('INVOICE_STATUS'), className: 'user-invoice-column' }
  ]

  return (
    <div>
      <LayoutContainer title={'CANCELLED_INVOICE_REPORTS'} breadCrumbsTitle={'cancelledInvoiceReports'}>
        <div className="cancelled-invoice-report-table-wrapper" data-testid="cancelled-invoice-report-table-wrapper">
          <DataTable value={getFetchedData} paginator={getFetchedData?.length} rows={rowCount} pageLinkSize={paginationButtons}>
            {columns.map((col, index) => (
              <Column key={index} field={col.field} header={col.header} body={col.body} className={col.className} />
            ))}
          </DataTable>
        </div>
      </LayoutContainer>
      <Dialog header={headerElement} visible={showModal} className="invoice-dialog-modal" onHide={() => setShowModal(false)}>
        <div data-testid="invoice-content">{ReactHtmlParser(invoiceContent)}</div>
      </Dialog>
      <DialogModal {...showDialog} onHide={handleDismiss} onDismiss={handleDismiss} />
    </div>
  )
}

export default Loader(CancelledInvoiceReports)
